import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
  };

  return (
    <>
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/60">
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">

            {/* Desktop: Left nav links */}
            <div className="hidden md:flex items-center gap-1" ref={dropdownRef}>
              <Link
                to="/"
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isActive('/') ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                Home
              </Link>
              <Link
                to="/acbuy-spreadsheet-guide"
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  isActive('/acbuy-spreadsheet-guide') ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                Shopping Guide
              </Link>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-background-100 text-foreground-800 hover:bg-background-200"
                >
                  <i className="ri-menu-line text-base"></i>
                  <span>All Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto z-50">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-foreground-800 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile: Hamburger */}
            <div className="md:hidden">
              <button
                onClick={() => {
                  setMobileMenuOpen(!mobileMenuOpen);
                  if (mobileMenuOpen) setMobileCatOpen(false);
                }}
                className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center cursor-pointer active:scale-95 transition-transform duration-150"
                aria-label="Toggle menu"
              >
                <i className={`text-lg text-foreground-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>

            {/* Center: Logo */}
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                <i className="ri-store-2-line text-white text-sm"></i>
              </div>
              <span className="text-lg font-bold font-heading whitespace-nowrap text-foreground-900">
                acbuy.my
              </span>
            </Link>

            {/* Desktop: Right CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-foreground-900 text-background-50 hover:bg-foreground-800"
              >
                ACBuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-primary-500 text-background-50 hover:bg-primary-600"
              >
                Buy It Now
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>
            </div>

            {/* Mobile: Buy It Now */}
            <div className="md:hidden">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 active:scale-95 transition-transform duration-150"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Slide-out Panel — rendered OUTSIDE nav to avoid backdrop-blur clipping */}
      <div
        className={`md:hidden fixed top-0 right-0 z-50 h-full w-[300px] max-w-[85vw] bg-background-50 shadow-2xl overflow-y-auto transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Panel Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/60">
          <Link to="/" onClick={closeMobileMenu} className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-store-2-line text-white text-sm"></i>
            </div>
            <span className="text-base font-bold font-heading text-foreground-900">acbuy.my</span>
          </Link>
          <button
            onClick={closeMobileMenu}
            className="w-9 h-9 rounded-full bg-background-100 flex items-center justify-center cursor-pointer active:scale-95 transition-transform duration-150"
            aria-label="Close menu"
          >
            <i className="ri-close-line text-lg text-foreground-700"></i>
          </button>
        </div>

        {/* Panel Content */}
        <div className="px-4 py-4 space-y-1">
          <Link
            to="/"
            onClick={closeMobileMenu}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold transition-colors duration-150 ${
              isActive('/') ? 'bg-primary-50 text-primary-700' : 'text-foreground-800 hover:bg-background-100'
            }`}
          >
            <i className="ri-home-4-line text-lg"></i>
            Home
          </Link>

          <Link
            to="/acbuy-spreadsheet-guide"
            onClick={closeMobileMenu}
            className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold transition-colors duration-150 ${
              isActive('/acbuy-spreadsheet-guide') ? 'bg-primary-50 text-primary-700' : 'text-foreground-800 hover:bg-background-100'
            }`}
          >
            <i className="ri-book-open-line text-lg"></i>
            Shopping Guide
          </Link>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={closeMobileMenu}
            className="flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors duration-150"
          >
            <i className="ri-external-link-line text-lg"></i>
            ACBuy Spreadsheet
          </a>

          {/* Divider */}
          <div className="pt-4 mt-3 border-t border-background-200">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-sm font-semibold text-foreground-800 hover:bg-background-100 transition-colors duration-150"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg"></i>
                All Categories
              </span>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-base ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {/* Animated category list */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-out ${
                mobileCatOpen ? 'max-h-[800px] opacity-100 mt-1' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="ml-4 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-300 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="pt-6 mt-4 border-t border-background-200">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobileMenu}
              className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 active:scale-[0.97] transition-transform duration-150"
            >
              <i className="ri-shopping-bag-line text-base"></i>
              Buy It Now
            </a>
          </div>
        </div>
      </div>
    </>
  );
}